// Dependencies
import React from 'react';

// Hooks
import { useRouter } from 'next/router';
import useWindowDimensions from 'src/hooks/useWindowDimensions';

// Material UI Components
import Box from '@mui/material/Box';

// Internal Components
import NavBar from './navbar/Navbar';
import DashboardHeader from './header';
import GoTop from 'src/components/GoTop';

// Configs
import { HEADER } from 'src/config';
import { IntercomChat } from 'src/components/IntercomChat';

// Types
type Props = {
  children: React.ReactNode;
};

/**
 * Panel layout component
 */
export default function PanelLayout({ children }: Props) {
  // =={ States }===============================================================
  // Sidebar state
  const [open, setOpen] = React.useState(false);

  // =={ Hooks }===============================================================
  const isDesktop = useWindowDimensions()?.width >= 1200;
  const router = useRouter();

  // =={ Effects }===============================================================
  // Close sidebar when the route changes
  React.useEffect(() => {
    setOpen(false);
  }, [router.pathname, router.query, router.asPath, router.locale]);

  // =={ Renders }===============================================================
  return (
    <Box
      display={{ lg: 'flex' }}
      pr={{ lg: 3 }}
      py={{ lg: 3 }}
      pl={{ lg: 34 }}
      sx={{
        pt: {
          xs: `${HEADER.MOBILE_HEIGHT}px`,
        },
        pb: {
          xs: `${HEADER.MOBILE_HEIGHT}px`,
        },
      }}
      minHeight={{
        lg: '100vh',
      }}
      overflow="hidden"
      bgcolor={(theme) => theme.palette.background.default}
      color={(theme) => theme.palette.text.primary}
    >
      <GoTop />

      <IntercomChat />

      {!isDesktop && <DashboardHeader onOpenSidebar={() => setOpen(!open)} />}

      {/** Navbar */}
      {(open || isDesktop) && (
        <Box
          sx={{
            position: 'fixed',
            transform: 'translate3d(0, 0, 0)',
            overflow: 'hidden',
            perspective: 1000,

            top: 0,
            left: 0,
            width: { lg: 34 * 8 },
            height: '100%',
            overflowY: 'auto',
            overflowX: 'hidden',
            zIndex: 1000,
          }}
        >
          <NavBar sideBar={open || isDesktop} onSideBar={setOpen} />
        </Box>
      )}

      {/** Content */}
      <Box
        component="main"
        flexGrow={1}
        minHeight={{
          lg: '100%',
        }}
        bgcolor={(theme) => theme.palette.background.default}
        borderRadius={{ lg: 3 }}
        border={(theme) => ({
          lg: `1px dashed ${theme.palette.divider}`,
        })}
        // py={3}
        pt={{
          lg: 3,
          //   xs: 3,
        }}
        width="100%"
      >
        {children}
      </Box>
    </Box>
  );
}
